import { Preset } from '../../../common/models/valueObjects/Preset';
import { isKeyOf } from '../../../common/utils/guards';
import { GQLIdEncoder, ModelType } from '../../../common/utils/GQLIdEncoder';
import { DateUtils } from '../../../common/utils/DateUtils';
import { DateRange } from '../../../common/dashboards/types';

type FilterType = {
  tags: Preset[];
  projects: Preset[];
  profiles: Preset[];
  dateRange: DateRange;
  dateRangeLabel: string;
  overviewTypes: Preset[];
  exceptions: Preset[];
};

type InputFilterType = {
  [Property in keyof FilterType]?: FilterType[Property];
};

type RawFilterType = {
  [Property in keyof FilterType]?: unknown;
};

type FilterPreset = {
  id: string;
  label: string;
  category?: string;
};

export class QueryFilterMapper {
  private readonly idEncoder = new GQLIdEncoder();

  parseQueryValue(rawData: unknown): InputFilterType {
    try {
      if (typeof rawData !== 'object' || !rawData) {
        return {};
      }

      const data = rawData as RawFilterType;

      const dateRange = this.parseDateRange(data.dateRange);
      const dateRangeLabel = dateRange ? this.parseDateRangeLabel(data.dateRangeLabel) : undefined;

      return {
        tags: this.parsePresetArray(data.tags, false),
        projects: this.parseTypedArray(data.projects, ModelType.Project, false),
        profiles: this.parseTypedArray(data.profiles, ModelType.Profile),
        overviewTypes: this.parsePresetArray(data.overviewTypes, false),
        exceptions: this.parsePresetArray(data.exceptions, false),
        dateRange,
        dateRangeLabel,
      };
    } catch {
      // if invalid filter map, return blank object
      return {};
    }
  }

  private parseDateRange(dateRange: unknown): DateRange | undefined {
    if (!Array.isArray(dateRange) || dateRange.length !== 2 || !dateRange.every((el) => typeof el === 'string')) {
      return undefined;
    }

    const [startDate, endDate] = dateRange as (string | null)[];

    if (startDate === null || endDate === null) {
      return undefined;
    }

    const range = [DateUtils.toMoment(startDate), DateUtils.toMoment(endDate)] as DateRange;

    // if any date is broken or null, default to non provided value
    if (range.some((date) => !date.isValid())) {
      return undefined;
    }
    return range;
  }

  private parseDateRangeLabel(value: unknown): string | undefined {
    if (typeof value !== 'string') {
      return undefined;
    }

    return value;
  }

  private parseTypedArray(data: unknown, type: ModelType, withCategory = true): Preset[] | undefined {
    const items = this.parsePresetArray(data, withCategory);
    if (items === undefined) {
      return undefined;
    }

    const areAllValidIds = items.every((item) => this.isValidId(item.getId(), type));
    if (!areAllValidIds) {
      return undefined;
    }

    return items;
  }

  private isValidId(encodedValue: string, modelType: ModelType): boolean {
    const id = this.idEncoder.decode(encodedValue);

    if (isNaN(id)) {
      return false;
    }

    const expectedValue = this.idEncoder.encode(modelType, id);

    return expectedValue === encodedValue;
  }

  private parsePresetArray(data: unknown, withCategory = true): Preset[] | undefined {
    if (!Array.isArray(data)) {
      return undefined;
    }

    const output: Preset[] = [];

    const itemKeys = ['id', 'label'];
    if (withCategory) {
      itemKeys.push('category');
    }

    for (const item of data) {
      for (const itemKey of itemKeys) {
        // if anything is missing, return blank array, we don't want to deal with misconfigurations.
        if (!isKeyOf(itemKey, item)) {
          return undefined;
        }
      }

      const { id, label } = item;
      let category = '';
      if (withCategory) {
        category = item.category;
      }

      output.push(new Preset(id, label, category));
    }

    return output;
  }

  // Mapping
  mapFilters(filters: FilterType) {
    return {
      tags: filters.tags.map((tag) => this.mapPreset(tag, false)),
      projects: filters.projects.map((project) => this.mapPreset(project, false)),
      profiles: filters.profiles.map((profile) => this.mapPreset(profile)),
      exceptions: filters.exceptions.map((exception) => this.mapPreset(exception, false)),
      overviewTypes: filters.overviewTypes.map((type) => this.mapPreset(type, false)),
      dateRange: filters.dateRange.map((date) => DateUtils.toISOString(date)),
      dateRangeLabel: filters.dateRangeLabel,
    };
  }

  private mapPreset(preset: Preset, withCategory = true) {
    const output: FilterPreset = {
      id: preset.getId(),
      label: preset.getLabel(),
    };

    if (withCategory) {
      output.category = preset.getCategory();
    }

    return output;
  }
}
